/**
 * @fileoverview Estilos da área de Configurações: menu lateral, cabeçalho da
 * seção e estados de carregando/erro. Monocromática, nos tokens neutros da
 * triagem (os mesmos da tela de OS); a seleção no menu é grafite, sem cor.
 *
 * Computador: duas colunas com rolagem própria (o menu fica sempre à vista).
 * Celular (< 960px, onde o app troca o menu pela barra de baixo): uma coisa por
 * vez, a lista ou a seção, rolando no próprio MainContainer.
 */

import { makeStyles } from "@material-ui/core/styles";

import { tokens } from "../../components/Triage/styles";

const NAV_WIDTH = 248;
// Recolhido fica só a coluna dos ícones: 60px é a largura em que o ícone fica
// centrado sem mudar o recuo dos itens, então nada pula de lugar na animação.
const NAV_COLLAPSED_WIDTH = 60;
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";
const motion = (transition) => ({
  transition,
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

const useSettingsStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const c = tokens(dark);
  const sm = theme.breakpoints.down("sm");
  const selected = dark ? "rgba(255,255,255,0.09)" : "rgba(15,23,42,0.07)";
  // Foco de teclado em itens e botões (não é campo: o anel é visível de propósito).
  const focusRing = `0 0 0 2px ${dark ? "rgba(255,255,255,0.4)" : "rgba(15,23,42,0.4)"}`;
  // O public/index.html pinta todo <a> (e a:visited) de verde com !important,
  // pensando nos links das mensagens: os itens do menu são links e precisam
  // vencer essa regra, também no estado "visitado".
  const linkColor = (color) => ({
    color: `${color} !important`,
    "&:visited": { color: `${color} !important` },
  });

  const iconButton = {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "none",
    background: "transparent",
    color: c.textSecondary,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    transition: "background 120ms, color 120ms",
    "&:hover": { background: c.hover, color: c.textPrimary },
    "&:focus-visible": { outline: "none", boxShadow: focusRing },
  };

  return {
    // <MainContainer className>: sem isto o mínimo do flex corta o fim das
    // colunas quando há avisos no topo (impersonação, período de teste).
    container: { minHeight: 0 },
    layout: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      [sm]: { display: "block", flex: "none" },
    },
    navColumn: {
      width: NAV_WIDTH,
      flexShrink: 0,
      overflowY: "auto",
      overflowX: "hidden",
      ...theme.scrollbarStylesSoft,
      borderRight: `1px solid ${c.border}`,
      background: c.subtle,
      ...motion(`width 220ms ${EASE}`),
      [sm]: { width: "auto", overflow: "visible", borderRight: "none", background: "transparent" },
    },
    // Os textos somem devagar e a coluna estreita; os ícones ficam no lugar. A
    // busca vira um quadrado com a lupa (clicar abre o menu e foca o campo).
    navColumnCollapsed: {
      width: NAV_COLLAPSED_WIDTH,
      "& $navTitle, & $navGroupLabel, & $navItemLabel, & $navSearchInput, & $navSearchClear": {
        opacity: 0,
        pointerEvents: "none",
      },
      "& $navHeader": { padding: "0 2px" },
    },
    content: {
      flex: 1,
      minWidth: 0,
      overflowY: "auto",
      ...theme.scrollbarStyles,
      [sm]: { overflow: "visible" },
    },
    inner: {
      maxWidth: 920,
      padding: "28px 32px 48px",
      [sm]: { padding: "16px 16px 24px" },
    },
    // Seção com tabela (a equipe): mais largura que os formulários.
    innerWide: { maxWidth: 1280 },

    // Cabeçalho da seção
    header: { marginBottom: 20 },
    headerTop: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      minHeight: 20,
      marginBottom: 4,
    },
    eyebrow: { fontSize: 12, fontWeight: 500, color: c.textMuted },
    title: {
      margin: 0,
      fontSize: 22,
      fontWeight: 600,
      color: c.textPrimary,
      lineHeight: 1.3,
      [sm]: { fontSize: 20 },
    },
    description: { margin: "4px 0 0", fontSize: 13, color: c.textSecondary, lineHeight: 1.5 },
    autosave: { margin: "6px 0 0", fontSize: 12, color: c.textMuted },
    iconButton,
    backButton: {
      ...iconButton,
      width: "auto",
      height: 30,
      gap: 2,
      padding: "0 8px 0 2px",
      marginLeft: -4,
      fontSize: 13,
      fontWeight: 500,
      fontFamily: "inherit",
    },
    body: { display: "flex", flexDirection: "column", gap: 24 },

    // Menu lateral (no celular, a lista)
    nav: {
      padding: "20px 12px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 18,
      [sm]: { padding: "20px 16px 24px", gap: 20 },
    },
    navHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 4px 0 10px",
      minHeight: 32,
      ...motion(`padding 220ms ${EASE}`),
    },
    navTitle: {
      flex: 1,
      minWidth: 0,
      margin: 0,
      fontSize: 15,
      fontWeight: 600,
      color: c.textPrimary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      ...motion(`opacity 160ms ${EASE}`),
      [sm]: { fontSize: 22 },
    },
    navGroup: { display: "flex", flexDirection: "column", gap: 2 },
    navGroupLabel: {
      padding: "0 10px 6px",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: c.textMuted,
      whiteSpace: "nowrap",
      overflow: "hidden",
      ...motion(`opacity 160ms ${EASE}`),
    },
    navItem: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px",
      borderRadius: 8,
      fontSize: 13.5,
      fontWeight: 500,
      lineHeight: 1.35,
      whiteSpace: "nowrap",
      overflow: "hidden",
      ...linkColor(c.textSecondary),
      textDecoration: "none",
      transition: "background 120ms, color 120ms",
      "& svg": { flexShrink: 0, color: c.textMuted },
      "&:hover": { background: c.hover, ...linkColor(c.textPrimary) },
      "&:focus-visible": { outline: "none", boxShadow: focusRing },
      [sm]: { padding: "13px 12px", fontSize: 15, borderRadius: 10 },
    },
    navItemActive: {
      background: selected,
      ...linkColor(c.textPrimary),
      fontWeight: 600,
      "& svg": { color: c.textPrimary },
      "&:hover": { background: selected, ...linkColor(c.textPrimary) },
    },
    navItemLabel: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      ...motion(`opacity 160ms ${EASE}`),
    },

    // Busca
    navSearch: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: 8,
      height: 34,
      padding: "0 10px",
      borderRadius: 8,
      border: `1px solid ${c.inputBorder}`,
      background: dark ? "rgba(255,255,255,0.03)" : "#ffffff",
      color: c.textMuted,
      "& > svg": { flexShrink: 0 },
      "&:focus-within": {
        borderColor: dark ? "rgba(255,255,255,0.32)" : "rgba(15,23,42,0.4)",
        boxShadow: dark ? "0 0 0 1px rgba(91, 124, 255, 0.15)" : "0 0 0 1px rgba(61, 92, 253, 0.12)",
      },
      [sm]: { height: 42 },
    },
    navSearchInput: {
      flex: 1,
      minWidth: 0,
      height: "100%",
      padding: 0,
      border: "none",
      outline: "none",
      background: "transparent",
      color: c.textPrimary,
      fontSize: 13,
      fontFamily: "inherit",
      "&::placeholder": { color: c.textMuted, opacity: 1 },
      ...motion(`opacity 160ms ${EASE}`),
      // 16px no celular: menos que isso o iPhone dá zoom ao focar.
      [sm]: { fontSize: 16 },
    },
    navSearchClear: {
      ...iconButton,
      width: 22,
      height: 22,
      borderRadius: 6,
      marginRight: -4,
      ...motion(`opacity 160ms ${EASE}`),
    },
    // Recolhido: o quadrado inteiro vira o botão que abre a busca.
    navSearchOpen: {
      position: "absolute",
      inset: -1,
      border: "none",
      borderRadius: 8,
      background: "transparent",
      cursor: "pointer",
      "&:hover": { background: c.hover },
      "&:focus-visible": { outline: "none", boxShadow: focusRing },
    },
    navSubItem: {
      display: "block",
      padding: "5px 10px 5px 36px",
      borderRadius: 8,
      fontSize: 12.5,
      lineHeight: 1.35,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      ...linkColor(c.textMuted),
      textDecoration: "none",
      "&:hover": { background: c.hover, ...linkColor(c.textPrimary) },
      "&:focus-visible": { outline: "none", boxShadow: focusRing },
      [sm]: { padding: "10px 12px 10px 38px", fontSize: 14 },
    },
    searchEmpty: {
      padding: "4px 10px",
      fontSize: 12.5,
      lineHeight: 1.5,
      color: c.textSecondary,
      whiteSpace: "normal",
      "& p": { margin: "0 0 4px" },
    },
    srOnly: {
      position: "absolute",
      width: 1,
      height: 1,
      overflow: "hidden",
      clip: "rect(0 0 0 0)",
      whiteSpace: "nowrap",
    },

    // Campo aberto pela busca: um realce neutro que some sozinho.
    settingHighlight: {
      borderRadius: 10,
      animation: `$settingFlash 1600ms ${EASE}`,
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    },
    // A linha recebe o foco só para o teclado e o leitor de tela continuarem
    // dali; o realce já mostra onde ela está.
    "@global": {
      "[data-setting-id]:focus": { outline: "none" },
    },
    "@keyframes settingFlash": {
      "0%, 35%": { backgroundColor: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)" },
      "100%": { backgroundColor: "transparent" },
    },
  };
});

export default useSettingsStyles;
